export interface TemplateWarning {
  message: string;
}

export interface ParsedTemplate {
  warnings: TemplateWarning[];
}

interface PlaceholderRule {
  pattern: RegExp;
  requiresFormat: boolean;
}

const RULES: PlaceholderRule[] = [
  { pattern: /^ID$/i, requiresFormat: false },
  { pattern: /^RANDOM\[(\d+)\]$/i, requiresFormat: false },
  { pattern: /^PICKUPDATE(?::([^}]+))?$/i, requiresFormat: true },
  { pattern: /^INVOICEDATE(?::([^}]+))?$/i, requiresFormat: true },
  { pattern: /^CREATEDATE(?::([^}]+))?$/i, requiresFormat: true },
  { pattern: /^CREATEDDATE(?::([^}]+))?$/i, requiresFormat: true },
  { pattern: /^QUOTEDATE(?::([^}]+))?$/i, requiresFormat: true },
  { pattern: /^LOADBOARDDATE(?::([^}]+))?$/i, requiresFormat: true }
];

const CANONICAL_NAMES: Record<string, string> = {
  ID: 'ID',
  RANDOM: 'RANDOM',
  PICKUPDATE: 'PICKUPDATE',
  INVOICEDATE: 'INVOICEDATE',
  CREATEDATE: 'CREATEDATE',
  CREATEDDATE: 'CREATEDDATE',
  QUOTEDATE: 'QUOTEDATE',
  LOADBOARDDATE: 'LOADBOARDDATE'
};

const PLACEHOLDER_RE = /\{([^}]*)\}/g;

export function parseTemplate(template: string): ParsedTemplate {
  const warnings: TemplateWarning[] = [];
  if (!template) return { warnings };

  const matches = Array.from(template.matchAll(PLACEHOLDER_RE));
  for (const m of matches) {
    const inner = m[1];
    if (!inner) {
      warnings.push({ message: `Empty placeholder "${m[0]}" — remove or fill in.` });
      continue;
    }

    const matched = RULES.find(r => r.pattern.test(inner));
    if (!matched) {
      warnings.push({
        message: `Unrecognized placeholder "${m[0]}". Supported: {ID}, {RANDOM[N]}, {PICKUPDATE:fmt}, {INVOICEDATE:fmt}, {CREATEDATE:fmt}, {QUOTEDATE:fmt}, {LOADBOARDDATE:fmt}.`
      });
      continue;
    }

    if (matched.requiresFormat) {
      const fmtMatch = inner.match(/:([^}]+)$/);
      if (!fmtMatch || !fmtMatch[1]) {
        warnings.push({
          message: `"${m[0]}" needs a date format after a colon, e.g. ${m[0].replace('}', ':yyyyMMdd}')}.`
        });
      }
    }
  }

  return { warnings };
}

export function canonicalizeTemplate(template: string): string {
  if (!template) return template;
  return template.replace(/\{([^}]*)\}/g, (whole, inner: string) => {
    if (!inner) return whole;
    const colonIdx = inner.indexOf(':');
    const bracketIdx = inner.indexOf('[');
    let nameEnd = inner.length;
    if (colonIdx >= 0) nameEnd = Math.min(nameEnd, colonIdx);
    if (bracketIdx >= 0) nameEnd = Math.min(nameEnd, bracketIdx);
    const name = inner.slice(0, nameEnd);
    const rest = inner.slice(nameEnd);
    const canonical = CANONICAL_NAMES[name.toUpperCase()];
    if (!canonical) return whole;
    return `{${canonical}${rest}}`;
  });
}
